// modules/offers/InKindOfferCreate.js
import React, { useState, useEffect } from 'react';
import {
  AutocompleteInput,
  Create,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  usePermissions,
  useNotify,
  useRedirect,
  useCreate,
  useInput,
  useUpdate,
  Edit,
} from "react-admin";
import { Box, Card, CardContent, Typography, Chip, Alert, Divider, Stack } from '@mui/material';
import { LocalOffer as OfferIcon, Redeem as DonateIcon, RequestQuote as NeedIcon } from '@mui/icons-material';
import { useEffect as useEffectReact, useRef } from "react";

const token = JSON.parse(localStorage.getItem('token'));

// Custom User Input Component (reused from your existing component)
const UserPhoneInput = (props) => {
  const { source, validate } = props;
  const { field, fieldState } = useInput({ source, validate });
  const notify = useNotify();

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const timerRef = useRef();

  const searchUsers = async (searchText) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/admin/users?search=${encodeURIComponent(searchText)}&page=1&limit=25`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  };

  useEffectReact(() => {
    if (inputValue.length < 3) {
      setOptions([]);
      setShowDropdown(false);
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        console.log('Searching for users with:', inputValue);
        const users = await searchUsers(inputValue);
        console.log('Search results:', users);
        setOptions(users);
        setShowDropdown(users.length > 0);
      } catch (error) {
        console.error('Error searching users:', error);
        notify('Error searching users', { type: 'error' });
        setOptions([]);
        setShowDropdown(false);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [inputValue, notify]);

  // Load initial value if editing
  useEffectReact(() => {
    if (field.value && typeof field.value === 'string') {
      fetch(`${process.env.REACT_APP_URL}/admin/users/${field.value}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })
        .then(response => response.json())
        .then(user => {
          setOptions([user]);
          setSelectedUser(user);
          setInputValue(`${user.phonenumber}${user.name ? ` - ${user.name}` : ''}`);
        })
        .catch(error => {
          console.error('Error fetching user:', error);
        });
    }
  }, [field.value]);

  const handleInputChange = (event) => {
    const newInputValue = event.target.value;
    setInputValue(newInputValue);

    if (newInputValue !== selectedUser?.phonenumber) {
      setSelectedUser(null);
      field.onChange('');
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setInputValue(`${user.phonenumber}${user.name ? ` - ${user.name}` : ''}`);
    field.onChange(user.id);
    setShowDropdown(false);
  };

  const handleInputFocus = () => {
    if (options.length > 0 && inputValue.length >= 3) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => setShowDropdown(false), 150);
  };

  const handleDropdownMouseDown = (event) => {
    event.preventDefault();
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setInputValue('');
    field.onChange('');
    setOptions([]);
  };

  return (
    <div className="space-y-2 relative">
      <label className="block text-sm font-medium text-gray-700">User Phone *</label>

      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder="Type at least 3 characters to search users by phone number..."
          className={`bg-white w-full px-3 py-2 border rounded-md shadow-sm ${fieldState.error ? 'border-red-500' : 'border-gray-300'
            } ${selectedUser ? 'bg-green-50' : ''}`}
          disabled={loading}
        />

        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}

        {selectedUser && !loading && (
          <button
            type="button"
            onClick={handleClearSelection}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}
      </div>

      {showDropdown && options.length > 0 && (
        <div
          className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
          onMouseDown={handleDropdownMouseDown}
        >
          {options.map((user) => (
            <div
              key={user.id}
              onClick={() => handleUserSelect(user)}
              className="px-4 py-2 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors duration-150"
            >
              <div className="font-medium text-gray-900">{user.phonenumber}</div>
              {user.name && (
                <div className="text-sm text-gray-600">{user.name}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDropdown && inputValue.length >= 3 && options.length === 0 && !loading && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-4 py-2 text-gray-500 text-sm">
            No users found for "{inputValue}"
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="p-2 bg-green-50 border border-green-200 rounded-md">
          <p className="text-xs text-green-700">
            ✓ Selected: <span className="font-semibold">{selectedUser.phonenumber}</span>
            {selectedUser.name && ` - ${selectedUser.name}`}
          </p>
        </div>
      )}

      {fieldState.error && (
        <p className="text-sm text-red-600">{fieldState.error.message}</p>
      )}

      <p className="text-xs text-gray-500">
        Start typing a phone number (minimum 3 characters) to search users
      </p>
    </div>
  );
};

// Main InKindOfferCreate Component
export const InKindOfferCreate = (props) => {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();
  const [offerType, setOfferType] = useState('donate');

  const transform = (data) => {
    console.log('Original form data:', data);

    const transformed = {
      owner: data.owner,
      category_id: Number(data.category_id),
      subCategory_id: Number(data.subCategory_id),
      offer: data.offer,
      quantity: data.quantity,
      price: data.price ? Number(data.price) : 0, // Set to 0 for in-kind
      unit: String(data.unit),
      price_per_unit: 0, // Set to 0 for in-kind
      location: data.location || undefined,
      offer_type: data.offer_type || 'donate',
      currency: 'SDG',
      state: String(data.state),
      locality: data.locality ? String(data.locality) : null,
      city: String(data.city), // City is required
      market: data.market ? String(data.market) : null,
      is_in_kind: true, // Always true for in-kind
      status: 'active',
      // Add in-kind specific fields
      notes: data.notes || undefined,
      images: data.images ? data.images.split(',').map(img => img.trim()).filter(img => img) : [],
    };

    console.log('Transformed in-kind data:', transformed);
    return transformed;
  };

  const handleSubmit = async (data) => {
    try {
      const transformedData = transform(data);
      
      // Create the in-kind offer
      await create('offers', {
        data: transformedData,
      });
      
      notify('In-kind offer created successfully!', { type: 'success' });
      redirect('/reports/in-kind-offers');
    } catch (error) {
      console.error('Error creating in-kind offer:', error);
      notify(error.message || 'Failed to create in-kind offer', { type: 'error' });
    }
  };

  const canEdit = permissions === "admin" || permissions === "operater";

  if (!canEdit) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to create in-kind offers.
        </Alert>
      </Box>
    );
  }

  return (
    <Create {...props} title="Create In-Kind Offer">
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <OfferIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Create In-Kind Offer
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create a donation or in-kind contribution request
                </Typography>
              </Box>
              <Chip 
                label="In-Kind" 
                color="primary" 
                variant="outlined"
                icon={<DonateIcon />}
              />
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>In-Kind Offers</strong> are non-monetary contributions or requests. 
                Donations represent items you want to give away, while Needs represent items you're looking for.
              </Typography>
            </Alert>

            <SimpleForm
              onSubmit={handleSubmit}
              toolbar={null}
              className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md"
            >
              {/* Header Section */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider />
              </div>

              {/* Row 1: User, Category, Subcategory */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <UserPhoneInput source="owner" validate={[required()]} />

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Category *</label>
                  <ReferenceInput source="category_id" reference="category" validate={[required()]}>
                    <AutocompleteInput
                      optionText="name"
                      optionValue="id"
                      label={false}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </ReferenceInput>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Subcategory *</label>
                  <ReferenceInput source="subCategory_id" reference="sub_category" validate={[required()]}>
                    <AutocompleteInput
                      optionText="name"
                      optionValue="id"
                      label={false}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </ReferenceInput>
                </div>
              </div>

              {/* Row 2: Offer Type and Description */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Offer Type *</label>
                  <SelectInput
                    source="offer_type"
                    choices={[
                      { id: "donate", name: "Donation (I want to give)" },
                      { id: "need", name: "Need (I'm looking for)" },
                    ]}
                    defaultValue="donate"
                    onChange={(value) => setOfferType(value)}
                    label={false}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    validate={[required()]}
                  />
                </div>

                <div className="md:col-span-3 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {offerType === 'donate' ? 'What are you donating? *' : 'What do you need? *'}
                  </label>
                  <TextInput
                    multiline
                    rows={2}
                    source="offer"
                    validate={[required()]}
                    label={false}
                    fullWidth
                    placeholder={
                      offerType === 'donate' 
                        ? 'Describe the item you want to donate (e.g., "Fresh tomatoes from my farm")' 
                        : 'Describe what you need (e.g., "Looking for wheat seeds for planting")'
                    }
                    className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Row 3: Quantity and Unit */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Quantity *</label>
                  <TextInput
                    source="quantity"
                    validate={[required()]}
                    label={false}
                    placeholder="e.g., 10 kg, 5 bags, 100 pieces"
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Unit *</label>
                  <ReferenceInput source="unit" reference="unit" validate={[required()]}>
                    <SelectInput
                      optionText="name"
                      optionValue="id"
                      label={false}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </ReferenceInput>
                </div>
              </div>

              {/* Row 4: Detailed Description */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detailed Description *
                </label>
                <TextInput
                  multiline
                  rows={3}
                  source="description"
                  validate={[required()]}
                  label={false}
                  fullWidth
                  placeholder={
                    offerType === 'donate'
                      ? 'Provide more details about your donation: condition, quality, storage requirements, etc.'
                      : 'Provide more details about what you need: specific requirements, quality standards, timeline, etc.'
                  }
                  className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {/* In-Kind Specific Section */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  In-Kind Specific Information
                </Typography>
                <Divider />
                
                <div className="mt-4 space-y-4">
                  <Alert severity="info">
                    <Typography variant="body2">
                      This is an <strong>in-kind</strong> {offerType === 'donate' ? 'donation' : 'request'}. 
                      No monetary value will be associated with this offer.
                    </Typography>
                  </Alert>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Additional Notes
                    </label>
                    <TextInput
                      multiline
                      rows={2}
                      source="notes"
                      label={false}
                      fullWidth
                      placeholder="Any special instructions, conditions, or notes about this in-kind offer..."
                      className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Image URLs (Optional)
                    </label>
                    <TextInput
                      multiline
                      rows={2}
                      source="images"
                      label={false}
                      fullWidth
                      placeholder="Enter image URLs separated by commas (max 5 images)"
                      helperText="Provide links to images of the item"
                      className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Location Section */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  Location Information
                </Typography>
                <Divider />
                
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">State *</label>
                    <ReferenceInput source="state" reference="state" validate={[required()]}>
                      <SelectInput
                        optionText="name"
                        optionValue="id"
                        label={false}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </ReferenceInput>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">City *</label>
                    <ReferenceInput source="city" reference="city" validate={[required()]}>
                      <SelectInput
                        optionText="name"
                        optionValue="id"
                        label={false}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </ReferenceInput>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Locality (Optional)</label>
                    <ReferenceInput source="locality" reference="locality">
                      <SelectInput
                        optionText="name"
                        optionValue="id"
                        label={false}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </ReferenceInput>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Market (Optional)</label>
                    <ReferenceInput source="market" reference="market">
                      <SelectInput
                        optionText="name"
                        optionValue="id"
                        label={false}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </ReferenceInput>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Detailed Location</label>
                    <TextInput
                      source="location"
                      label={false}
                      fullWidth
                      placeholder="Specific address, landmark, or location details"
                      className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <Typography variant="body2" color="text.secondary">
                  This will create an <strong>in-kind</strong> {offerType === 'donate' ? 'donation' : 'need'} that will appear in the in-kind dashboard.
                </Typography>
                <SaveButton
                  label={offerType === 'donate' ? 'Create Donation' : 'Create Need Request'}
                  className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-md shadow-sm transition duration-200"
                />
              </div>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Create>
  );
};

// Edit Component
export const InKindOfferEdit = (props) => {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const redirect = useRedirect();
  const [update] = useUpdate();
  const [offerType, setOfferType] = useState('donate');

  const transform = (data) => {
    console.log('Original form data:', data);

    const transformed = {
      owner: data.owner,
      category_id: Number(data.category_id),
      subCategory_id: Number(data.subCategory_id),
      offer: data.offer,
      quantity: data.quantity,
      price: 0, // Always 0 for in-kind
      unit: String(data.unit),
      price_per_unit: 0, // Always 0 for in-kind
      location: data.location || undefined,
      offer_type: data.offer_type || 'donate',
      currency: 'SDG',
      state: String(data.state),
      locality: data.locality ? String(data.locality) : null,
      city: String(data.city),
      market: data.market ? String(data.market) : null,
      is_in_kind: true, // Always true
      status: data.status || 'active',
      notes: data.notes || undefined,
      images: data.images ? data.images.split(',').map(img => img.trim()).filter(img => img) : [],
    };

    console.log('Transformed in-kind data:', transformed);
    return transformed;
  };

  const handleSubmit = async (data) => {
    try {
      const transformedData = transform(data);
      
      await update('offers', {
        id: props.id,
        data: transformedData,
      });
      
      notify('In-kind offer updated successfully!', { type: 'success' });
      redirect('/reports/in-kind-offers');
    } catch (error) {
      console.error('Error updating in-kind offer:', error);
      notify(error.message || 'Failed to update in-kind offer', { type: 'error' });
    }
  };

  const canEdit = permissions === "admin" || permissions === "operater";

  if (!canEdit) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You don't have permission to edit in-kind offers.
        </Alert>
      </Box>
    );
  }

  return (
    <Edit {...props} title="Edit In-Kind Offer">
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 2 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <OfferIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Edit In-Kind Offer
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update the details of this in-kind contribution
                </Typography>
              </Box>
              <Chip 
                label="In-Kind" 
                color="primary" 
                variant="outlined"
                icon={<DonateIcon />}
              />
            </Stack>

            <SimpleForm
              onSubmit={handleSubmit}
              toolbar={null}
              className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md"
            >
              {/* Same form structure as Create, but with existing values */}
              {/* You can reuse the same form fields as InKindOfferCreate */}
              {/* Just make sure to handle existing data properly */}
              
              {/* Basic Information */}
              <div className="mb-8">
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
                <Divider />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <UserPhoneInput source="owner" validate={[required()]} />

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Category *</label>
                  <ReferenceInput source="category_id" reference="category" validate={[required()]}>
                    <AutocompleteInput
                      optionText="name"
                      optionValue="id"
                      label={false}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </ReferenceInput>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Subcategory *</label>
                  <ReferenceInput source="subCategory_id" reference="sub_category" validate={[required()]}>
                    <AutocompleteInput
                      optionText="name"
                      optionValue="id"
                      label={false}
                      className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </ReferenceInput>
                </div>
              </div>

              {/* Offer Type and Description */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Offer Type *</label>
                  <SelectInput
                    source="offer_type"
                    choices={[
                      { id: "donate", name: "Donation (I want to give)" },
                      { id: "need", name: "Need (I'm looking for)" },
                    ]}
                    onChange={(value) => setOfferType(value)}
                    label={false}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    validate={[required()]}
                  />
                </div>

                <div className="md:col-span-3 space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {offerType === 'donate' ? 'What are you donating? *' : 'What do you need? *'}
                  </label>
                  <TextInput
                    multiline
                    rows={2}
                    source="offer"
                    validate={[required()]}
                    label={false}
                    fullWidth
                    className="border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Status field for Edit mode */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <SelectInput
                  source="status"
                  choices={[
                    { id: "active", name: "Active" },
                    { id: "inactive", name: "Inactive" },
                    { id: "in_progress", name: "In Progress" },
                  ]}
                  label={false}
                  className="w-full md:w-1/4 border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  validate={[required()]}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
                <SaveButton
                  label="Update In-Kind Offer"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md shadow-sm transition duration-200"
                />
              </div>
            </SimpleForm>
          </CardContent>
        </Card>
      </Box>
    </Edit>
  );
};

export default InKindOfferCreate;